import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '@/components/ui/Header';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { findTrainingById } from './trainingsData';
import useTrainingProgress from './hooks/useTrainingProgress';

const TrainingDetailPage = () => {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const { progress, updateProgress, resetTraining } = useTrainingProgress();

  const training = useMemo(() => findTrainingById(trainingId), [trainingId]);
  const totalModules = training?.videos?.length ?? 0;

  const entry = progress?.[trainingId];
  const [activeIndex, setActiveIndex] = useState(() => {
    if (!totalModules) {
      return 0;
    }
    const savedIndex = entry?.currentIndex ?? 0;
    return Math.min(Math.max(savedIndex, 0), totalModules - 1);
  });
  const [completedModules, setCompletedModules] = useState(() => new Set(entry?.completedModules ?? []));

  useEffect(() => {
    if (!training) {
      return;
    }
    const savedIndex = entry?.currentIndex ?? 0;
    setActiveIndex(Math.min(Math.max(savedIndex, 0), totalModules - 1));
    setCompletedModules(new Set(entry?.completedModules ?? []));
  }, [entry?.completedModules, entry?.currentIndex, totalModules, training]);

  const notifications = useMemo(() => ({
    messages: 0,
    timeOff: 0,
  }), []);

  if (!training) {
    return (
      <div className="min-h-screen bg-background">
        <Header allowLocationSelection={false} notifications={notifications} />
        <main className="pt-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-card border border-border rounded-2xl p-8 text-center space-y-4">
              <Icon name="CircleAlert" size={32} className="mx-auto text-amber-500" />
              <h1 className="text-2xl font-semibold text-foreground">Training unavailable</h1>
              <p className="text-muted-foreground">
                The training you are looking for no longer exists or has been unpublished.
                Return to the catalog to pick another course.
              </p>
              <div className="flex justify-center">
                <Button iconName="ArrowLeft" onClick={() => navigate('/trainings')}>
                  Back to trainings
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const currentVideo = training.videos?.[activeIndex];
  const percentComplete = totalModules
    ? Math.round((completedModules.size / totalModules) * 100)
    : 0;

  const isModuleUnlocked = (index) => index === 0 || completedModules.has(index - 1);
  const isModuleComplete = (index) => completedModules.has(index);

  const setActiveModule = (nextIndex) => {
    setActiveIndex(nextIndex);
    updateProgress(trainingId, (previous = {}) => ({
      ...previous,
      currentIndex: nextIndex,
    }));
  };

  const handleModuleSelect = (index) => {
    if (!isModuleUnlocked(index)) {
      return;
    }
    setActiveModule(index);
  };

  const handleMarkWatched = () => {
    if (!training || totalModules === 0) {
      return;
    }
    const alreadyComplete = completedModules.has(activeIndex);
    if (alreadyComplete && activeIndex < totalModules - 1) {
      setActiveModule(activeIndex + 1);
      return;
    }

    const nextCompleted = new Set(completedModules);
    nextCompleted.add(activeIndex);

    const finished = nextCompleted.size === totalModules;
    const nextIndex = finished
      ? activeIndex
      : Math.min(activeIndex + 1, totalModules - 1);

    setCompletedModules(nextCompleted);
    setActiveIndex(nextIndex);

    updateProgress(trainingId, (previous = {}) => ({
      ...previous,
      completedModules: Array.from(nextCompleted),
      currentIndex: nextIndex,
      status: finished ? 'completed' : 'in-progress',
      completedAt: finished ? new Date().toISOString() : previous?.completedAt,
    }));
  };

  const handlePrevious = () => {
    if (activeIndex === 0) {
      return;
    }
    setActiveModule(activeIndex - 1);
  };

  const handleNext = () => {
    if (activeIndex >= totalModules - 1 || !isModuleUnlocked(activeIndex + 1)) {
      return;
    }
    setActiveModule(activeIndex + 1);
  };

  const handleReset = () => {
    resetTraining(trainingId);
    setCompletedModules(new Set());
    setActiveIndex(0);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header allowLocationSelection={false} notifications={notifications} />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
          <div className="flex flex-col gap-3">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              onClick={() => navigate('/trainings')}
              className="w-fit"
            >
              Back to catalog
            </Button>
            <div className="flex flex-col gap-2">
              <p className="text-sm uppercase tracking-widest text-muted-foreground">
                {training.category} • {training.difficulty} • {training.estimatedTime}
              </p>
              <h1 className="text-3xl font-bold text-foreground">{training.title}</h1>
              <p className="text-muted-foreground max-w-3xl">
                {training.description}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-8">
            <section className="space-y-6">
              <div className="bg-card border border-border rounded-3xl p-4 sm:p-6">
                <div className="aspect-video rounded-2xl overflow-hidden border border-border bg-black mb-4">
                  {currentVideo ? (
                    <iframe
                      key={currentVideo.youtubeId}
                      src={`https://www.youtube.com/embed/${currentVideo.youtubeId}?rel=0&modestbranding=1`}
                      title={currentVideo.title}
                      className="w-full h-full"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                      Video unavailable
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">
                        Module {activeIndex + 1} of {totalModules}
                      </p>
                      <h2 className="text-2xl font-semibold text-foreground">
                        {currentVideo?.title}
                      </h2>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Clock" size={16} />
                      <span>{currentVideo?.duration}</span>
                    </div>
                  </div>
                  <p className="text-muted-foreground">{currentVideo?.topic}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    iconName={isModuleComplete(activeIndex) ? 'CheckCircle' : 'Play'}
                    onClick={handleMarkWatched}
                    disabled={!currentVideo}
                    className="flex-1"
                  >
                    {isModuleComplete(activeIndex) ? 'Completed — continue anyway' : 'Mark watched & continue'}
                  </Button>
                  <Button
                    variant="secondary"
                    iconName="ChevronLeft"
                    onClick={handlePrevious}
                    disabled={activeIndex === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    iconName="ChevronRight"
                    iconPosition="right"
                    onClick={handleNext}
                    disabled={activeIndex >= totalModules - 1 || !isModuleUnlocked(activeIndex + 1)}
                  >
                    Next short
                  </Button>
                </div>
              </div>
            </section>

            <aside className="space-y-6">
              <div className="bg-card border border-border rounded-3xl p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completion</p>
                    <p className="text-3xl font-bold text-foreground">
                      {percentComplete}%
                    </p>
                  </div>
                  <span className={cn(
                    'px-3 py-1 rounded-full text-xs font-semibold',
                    percentComplete >= 100
                      ? 'bg-emerald-100 text-emerald-700'
                      : percentComplete > 0
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-700',
                  )}
                  >
                    {percentComplete >= 100 ? 'Complete' : percentComplete > 0 ? 'In progress' : 'Not started'}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${percentComplete}%` }}
                  />
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Icon name="Layers" size={16} />
                  <span>
                    {completedModules.size}/{totalModules} shorts viewed sequentially
                  </span>
                </div>
                <Button
                  variant="ghost"
                  iconName="RotateCcw"
                  onClick={handleReset}
                  disabled={completedModules.size === 0}
                  className="w-full justify-center"
                >
                  Reset progress
                </Button>
              </div>

              <div className="bg-card border border-border rounded-3xl p-6 space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                  Modules
                </h3>
                <div className="space-y-3">
                  {training.videos.map((video, index) => {
                    const unlocked = isModuleUnlocked(index);
                    const complete = isModuleComplete(index);
                    const active = index === activeIndex;
                    return (
                      <button
                        type="button"
                        key={video.id}
                        onClick={() => handleModuleSelect(index)}
                        disabled={!unlocked}
                        className={cn(
                          'w-full text-left border rounded-2xl p-4 transition shadow-sm',
                          active && 'border-primary bg-primary/5',
                          complete && 'border-emerald-200 bg-emerald-50/60',
                          !unlocked && 'opacity-60 cursor-not-allowed',
                          'hover:border-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                        )}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-muted-foreground">
                              Step {index + 1}
                            </span>
                            {complete && (
                              <Icon name="CheckCircle" size={16} className="text-emerald-500" />
                            )}
                            {!complete && !unlocked && (
                              <Icon name="Lock" size={16} className="text-slate-400" />
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{video.duration}</span>
                        </div>
                        <p className="font-medium text-foreground">{video.title}</p>
                        <p className="text-sm text-muted-foreground">{video.topic}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-card border border-border rounded-3xl p-6 space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                  Learning goals
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {training.learningGoals.map((goal) => (
                    <li key={goal} className="flex items-start gap-2">
                      <Icon name="Sparkles" size={16} className="text-primary mt-0.5" />
                      <span>{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrainingDetailPage;
